import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Spinner, Table } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import NoAccessPage from './NoAccessPage';

Chart.register(...registerables);

const PERIODS = [7, 30, 90];

// Dark text on the light badges (info, warning): Bootstrap's white text on them is under 2:1
const OUTCOMES = {
    Succeeded: { label: 'Signed in', variant: 'success' },
    NeedsTwoFactor: { label: 'Sent to 2FA', variant: 'info', text: 'dark' },
    Failed: { label: 'Wrong password or code', variant: 'danger' },
    UnknownUser: { label: 'Unknown email', variant: 'warning', text: 'dark' },
    LockedOut: { label: 'Locked out', variant: 'danger' },
    NotAllowed: { label: 'Email not confirmed', variant: 'secondary' }
};

const FEEDBACK_TYPES = {
    Wrong: { label: "Something's wrong", variant: 'danger' },
    Unclear: { label: 'Unclear', variant: 'warning', text: 'dark' },
    Useful: { label: 'Useful', variant: 'success' },
    Idea: { label: 'Idea', variant: 'info', text: 'dark' }
};

const formatTime = (time) => time
    ? new Date(time).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '–';

const formatDate = (time) => new Date(time).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

// Route names as stored (Splash, AdvancedInsights, DeepDive) to readable labels
const pageLabel = (page) => page === 'Splash' ? 'Site list' : page.replace(/([a-z])([A-Z])/g, '$1 $2');

function Tile({ label, value }) {
    return (
        <div className="admin-tile">
            <div className="admin-tile-value">{value.toLocaleString()}</div>
            <div className="admin-tile-label">{label}</div>
        </div>
    );
}

// Super-admin page. Starts with portal usage; more admin tools will live here.
export function AdminPage() {
    const [days, setDays] = useState(30);
    const [usage, setUsage] = useState(null);
    const [status, setStatus] = useState('loading');

    useEffect(() => {
        setStatus('loading');
        fetch(`/api/AdminAPI/Usage?days=${days}&utcOffsetMinutes=${-new Date().getTimezoneOffset()}`)
            .then((response) => {
                if (response.status === 401 || response.status === 403) {
                    setStatus('forbidden');
                    return null;
                }
                if (!response.ok) {
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then((data) => {
                if (data) {
                    setUsage(data);
                    setStatus('ready');
                }
            })
            .catch(() => setStatus('error'));
    }, [days]);

    if (status === 'forbidden') {
        return <NoAccessPage />;
    }

    const chart = usage && {
        labels: usage.daily.map((d) => new Date(d.date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })),
        datasets: [
            { type: 'line', label: 'Logins', data: usage.daily.map((d) => d.logins), borderColor: '#14a4dd', backgroundColor: '#14a4dd', tension: 0.3 },
            { type: 'bar', label: 'Active users', data: usage.daily.map((d) => d.activeUsers), backgroundColor: '#002C4E', borderRadius: 3 }
        ]
    };

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <h2>Admin</h2>
                    <div className="text-muted">Portal usage</div>
                </div>
                <ButtonGroup>
                    {PERIODS.map((period) => (
                        <Button key={period} variant={period === days ? 'primary' : 'outline-primary'} onClick={() => setDays(period)}>
                            {period} days
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

            {status === 'loading' ? <Spinner animation="border" className="m-4" /> : <></>}
            {status === 'error' ? <Alert variant="warning">Couldn't load portal usage. Check the tracking tables exist (docs/sql/portal-activity-tables.sql).</Alert> : <></>}

            {status === 'ready' ? (
                <>
                    <div className="admin-tiles">
                        <Tile label="Logins" value={usage.totals.logins} />
                        <Tile label="Failed login attempts" value={usage.totals.failedLogins} />
                        <Tile label="Active users" value={usage.totals.activeUsers} />
                        <Tile label="Page views" value={usage.totals.pageViews} />
                    </div>

                    <Card className="admin-card">
                        <Card.Body>
                            <Card.Title>Report feedback</Card.Title>
                            {usage.feedback.length === 0 ? <div className="text-muted">No feedback on Advanced Insights reports in this period.</div> : (
                                <div className="admin-table-scroll">
                                    <Table size="sm" hover>
                                        <thead><tr><th>Time</th><th>From</th><th>About</th><th>Type</th><th>Comment</th></tr></thead>
                                        <tbody>
                                            {usage.feedback.map((item, i) => {
                                                const type = FEEDBACK_TYPES[item.type];
                                                return (
                                                    <tr key={i}>
                                                        <td>{formatTime(item.time)}</td>
                                                        <td>{item.name || item.email}<div className="text-muted small">{item.name ? item.email : ''}</div></td>
                                                        <td>
                                                            {item.section ? item.section + (item.heading ? ' › ' + item.heading : '') : 'Whole report'}
                                                            <div className="text-muted small">{item.site}{item.reportVersion ? ' · report of ' + formatDate(item.reportVersion) : ''}</div>
                                                        </td>
                                                        <td>{type ? <Badge bg={type.variant} text={type.text}>{type.label}</Badge> : <></>}</td>
                                                        <td className="admin-feedback-comment">{item.comment}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="admin-card">
                        <Card.Body>
                            <Card.Title>Chart ratings</Card.Title>
                            {usage.ratings.length === 0 ? <div className="text-muted">No charts rated in this period.</div> : (
                                <div className="admin-table-scroll">
                                    <Table size="sm" hover>
                                        <thead><tr><th>Chart</th><th className="text-end">Helpful</th><th className="text-end">Not helpful</th><th className="ps-4">Last vote</th></tr></thead>
                                        <tbody>
                                            {usage.ratings.map((rating, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        {rating.section ? rating.section + (rating.heading ? ' › ' + rating.heading : '') : 'Whole report'}
                                                        <div className="text-muted small">{rating.site}{rating.reportVersion ? ' · report of ' + formatDate(rating.reportVersion) : ''}</div>
                                                    </td>
                                                    <td className="text-end">{rating.helpful}</td>
                                                    <td className="text-end">{rating.notHelpful}</td>
                                                    <td className="ps-4">{formatTime(rating.lastVoted)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                            <div className="text-muted small mt-2">Each report version is rated separately. Each person's latest vote on a chart counts once.</div>
                        </Card.Body>
                    </Card>

                    <Card className="admin-card">
                        <Card.Body>
                            <Card.Title>Daily activity</Card.Title>
                            <div style={{ height: 260 }}>
                                <Bar data={chart} options={{ maintainAspectRatio: false, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }} />
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="admin-card">
                        <Card.Body>
                            <Card.Title>Users</Card.Title>
                            <div className="admin-table-scroll">
                                <Table size="sm" hover>
                                    <thead><tr><th>User</th><th>Last seen</th><th>Last login</th><th className="text-end">Logins</th><th className="text-end">Page views</th></tr></thead>
                                    <tbody>
                                        {usage.users.map((user, i) => (
                                            <tr key={i}>
                                                <td>{user.name || user.email}<div className="text-muted small">{user.name ? user.email : ''}</div></td>
                                                <td>{formatTime(user.lastSeen)}</td>
                                                <td>{formatTime(user.lastLogin)}</td>
                                                <td className="text-end">{user.logins}</td>
                                                <td className="text-end">{user.pageViews}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    <div className="admin-columns">
                        <Card className="admin-card">
                            <Card.Body>
                                <Card.Title>Pages</Card.Title>
                                <Table size="sm" hover>
                                    <thead><tr><th>Page</th><th className="text-end">Views</th><th className="text-end">Users</th></tr></thead>
                                    <tbody>
                                        {usage.pages.map((page) => (
                                            <tr key={page.page}><td>{pageLabel(page.page)}</td><td className="text-end">{page.views}</td><td className="text-end">{page.users}</td></tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                        <Card className="admin-card">
                            <Card.Body>
                                <Card.Title>Sites</Card.Title>
                                <Table size="sm" hover>
                                    <thead><tr><th>Site</th><th className="text-end">Views</th><th className="text-end">Users</th><th>Last seen</th></tr></thead>
                                    <tbody>
                                        {usage.sites.map((site) => (
                                            <tr key={site.site}><td>{site.site}</td><td className="text-end">{site.views}</td><td className="text-end">{site.users}</td><td>{formatTime(site.lastSeen)}</td></tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </div>

                    <Card className="admin-card">
                        <Card.Body>
                            <Card.Title>Recent login attempts</Card.Title>
                            <div className="admin-table-scroll">
                                <Table size="sm" hover>
                                    <thead><tr><th>Time</th><th>User</th><th>Outcome</th><th>IP address</th></tr></thead>
                                    <tbody>
                                        {usage.recentLogins.map((login, i) => {
                                            const outcome = OUTCOMES[login.outcome] || { label: login.outcome, variant: 'secondary' };
                                            return (
                                                <tr key={i}>
                                                    <td>{formatTime(login.time)}</td>
                                                    <td>{login.name || login.email || '–'}<div className="text-muted small">{login.name ? login.email : ''}</div></td>
                                                    <td><Badge bg={outcome.variant} text={outcome.text}>{outcome.label}</Badge></td>
                                                    <td>{login.ip}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </>
            ) : <></>}
        </div>
    );
}
